/**
 * Request Board — history event kinds and the alert-rule catalogue.
 *
 * The backend writes every history event and decides who is alerted (in app
 * and by email) from each person's saved rules; the events come back with
 * `notified` / `emailed` filled in. This file only names and describes them,
 * and lists the rules the Alerts dialog edits (the same keys the backend
 * validates).
 */
import { COL, COLUMNS, columnByKey } from "./requestBoardModel";

export const EVENT = {
  CREATED: "created",
  MOVED: "moved",
  REORDERED: "reordered",
  COMMENTED: "commented",
  IMAGES: "images",
  EDITED: "edited",
  DELETED: "deleted",
  // Backend details: the card moved into Requires Backend Changes with a summary /
  // spec / tasks (`edit: true` when the details were updated later), and a backend
  // task ticked or unticked (`done`).
  HANDOFF: "backend_details",
  CHECK: "check",
  // A question answered without code: the answer is saved and the card moves to
  // Completed - Awaiting Review in one step (`text` = the answer).
  ANSWERED: "answered",
  // Recurring Encounters — one per click (update request 2026-10-20).
  HIT: "hit",
};

export const EVENT_LABEL = {
  [EVENT.CREATED]: "New request",
  [EVENT.MOVED]: "Status change",
  [EVENT.REORDERED]: "Reordered",
  [EVENT.COMMENTED]: "Comment",
  [EVENT.IMAGES]: "Images",
  [EVENT.EDITED]: "Edited",
  [EVENT.DELETED]: "Deleted",
  [EVENT.HANDOFF]: "Backend details",
  [EVENT.CHECK]: "Backend task",
  [EVENT.ANSWERED]: "Answered",
  [EVENT.HIT]: "Recurring encounter",
};

/** The history filter chips: which event kinds each one shows. */
export const HISTORY_FILTERS = [
  { value: "all", label: "All", kinds: null },
  { value: "moves", label: "Status changes", kinds: [EVENT.MOVED, EVENT.REORDERED] },
  { value: "comments", label: "Comments", kinds: [EVENT.COMMENTED] },
  { value: "new", label: "New & deleted", kinds: [EVENT.CREATED, EVENT.DELETED] },
  { value: "hits", label: "Encounters", kinds: [EVENT.HIT] },
  {
    value: "edits",
    label: "Edits",
    kinds: [EVENT.EDITED, EVENT.IMAGES, EVENT.HANDOFF, EVENT.CHECK, EVENT.ANSWERED],
  },
];

const colName = (key) => columnByKey(key)?.label || key;

/** One readable sentence for an event — the history's Details column. */
export const describeEvent = (e) => {
  switch (e.kind) {
    case EVENT.CREATED:
      return `Filed in ${colName(e.to)}`;
    case EVENT.MOVED:
      return `${colName(e.from)} → ${colName(e.to)}`;
    case EVENT.REORDERED:
      return `Reordered within ${colName(e.to)}`;
    case EVENT.COMMENTED:
      return e.text || "Posted images";
    case EVENT.IMAGES:
    case EVENT.EDITED:
      return e.text;
    case EVENT.DELETED:
      return `Removed from ${colName(e.from)}`;
    case EVENT.HANDOFF:
      return e.edit
        ? `Updated the backend details: "${e.text}"`
        : `Moved to Requires Backend Changes: "${e.text}"`;
    case EVENT.CHECK:
      return `${e.done ? "Completed" : "Reopened"} "${e.text}"`;
    case EVENT.ANSWERED:
      return `Answered: "${e.text}"`;
    case EVENT.HIT:
      return "Logged a recurring encounter";
    default:
      return "";
  }
};

/* ─────────────────────────────── alerts ──────────────────────────────── */

/**
 * The alert rules, in the order the Alerts dialog lists them, grouped as it
 * groups them. Each rule has an Email and an In-app switch; `stages` rules also
 * carry the stages they watch. `scoped` rules obey "Cards to follow".
 */
export const ALERT_RULES = [
  {
    key: "involvedStage",
    group: "board",
    title: "A card I'm involved in changes stage",
    sub: "Cards you requested, are working on, or commented on",
  },
  { key: "enters", group: "board", title: "A card enters a stage", sub: "Only the stages you pick", stages: true, scoped: true },
  { key: "leaves", group: "board", title: "A card leaves a stage", sub: "Only the stages you pick", stages: true, scoped: true },
  { key: "urgent", group: "board", title: "A card is marked Urgent", sub: 'Uses the "Cards to follow" setting', scoped: true },
  { key: "comments", group: "board", title: "New comments on cards", sub: 'Uses the "Cards to follow" setting', scoped: true },
  { key: "mentions", group: "board", title: "Someone @mentions me", sub: "In any comment on the board" },
  {
    key: "approved",
    group: "review",
    title: "A card I requested or built is approved",
    sub: `It moves from ${COLUMNS.find((c) => c.key === COL.REVIEW).label} to ${COLUMNS.find((c) => c.key === COL.DONE).label}`,
  },
  {
    key: "sentBack",
    group: "review",
    title: "A card is sent back with changes",
    sub: `It moves from ${COLUMNS.find((c) => c.key === COL.REVIEW).label} back to ${COLUMNS.find((c) => c.key === COL.PROGRESS).label}`,
  },
];

export const ALERT_GROUPS = [
  { key: "board", label: "Board cards" },
  { key: "review", label: "Review" },
];

export const DELIVERY_MODES = [
  { key: "instant", label: "Right away" },
  { key: "hourly", label: "Hourly digest" },
  { key: "daily", label: "Daily digest" },
];

/**
 * Everyone starts with in-app alerts for the cards they're part of and email
 * only for @mentions — email is something a person turns up, not down.
 */
export const DEFAULT_PREFS = {
  scope: "mine", // "mine" (cards they requested, hold or commented on) | "all"
  rules: {
    involvedStage: { email: false, app: true },
    enters: { email: false, app: false, stages: [] },
    leaves: { email: false, app: false, stages: [] },
    urgent: { email: false, app: true },
    comments: { email: false, app: true },
    mentions: { email: true, app: true },
    approved: { email: false, app: true },
    sentBack: { email: false, app: true },
  },
  delivery: {
    mode: "instant",
    weekends: false,
  },
};

/** Rules with at least one channel on — the dialog's "6 of 7 on". */
export const rulesOn = (prefs) =>
  ALERT_RULES.filter((r) => prefs?.rules?.[r.key]?.email || prefs?.rules?.[r.key]?.app).length;

export const COLUMN_OPTIONS = COLUMNS.map((c) => ({ value: c.key, label: c.label }));
